// src/features/apps/AppPlatformUtils.ts
//
// Pure helpers for the generic platform rows (app_cfg_platform; Fire TV first):
// which row is the platform's, what the holder side vs an operator may send, what
// each of them sees of the store identity, and the store-field grammars the API
// applies. The API stays the authority on writes: it ASSIGNS the package when the
// platform is enabled, lets the holder side set the store app id once, and keeps
// every other store-field change for operators.

import {
  ANDROID_APPLICATION_ID_PATTERN,
  APPLE_BUNDLE_ID_PATTERN,
  APP_PLATFORM_CONSTRAINTS,
  APP_PLATFORM_INFO,
  type AppPlatformInfo,
  type AppPlatformPackageKind,
} from '@/lib/constants/appPlatform';
import { FACTORY_STORE_INFO, type FactoryStoreKey } from '@/lib/constants/appFactory';
import { APP_STATE_TYPE_ID } from '@/lib/constants/app';
import type {
  AppCfgPlatformCreateReq,
  AppCfgPlatformRow,
  AppCfgPlatformUpdateReq,
  AppCfgsDto,
  AppPlatformFormData,
} from '@/lib/types/App';

/** The two store-identity fields of the settings form. */
export type AppPlatformStoreField = 'storePackageId' | 'storeAppId';

/** A platform's limit on the app name and the sentence a longer one gets (AppPlatformUtils.titleLimit). */
export interface AppPlatformTitleLimit {
  max: number;
  message?: string;
}

/** The Readiness card's To-submit row for the listing's age rating (AppPlatformUtils.ageRatingReadiness). */
export interface AppAgeRatingReadiness {
  label: string;
  color: string;
  action: 'Choose' | 'Open';
}

/** The Readiness card's To-submit row for the listing's ads answer (AppPlatformUtils.adsReadiness). */
export interface AppAdsReadiness {
  label: 'Yes' | 'No' | 'Missing';
  color: string;
  action: 'Answer' | 'Open';
}

/** What the person may do with the store app id field (AppPlatformUtils.storeAppIdState). */
export type AppPlatformStoreAppIdState = 'editable' | 'readonly' | 'needs-package';

export class AppPlatformUtils {
  /**
   * The API carries the platform rows. An API that predates them sends no
   * `platforms` key at all — the platform tabs stay absent then, because their
   * routes do not exist there either.
   */
  static isAvailable(app: AppCfgsDto): boolean {
    return Array.isArray(app.platforms);
  }

  /** The app's row for one platform; null = not enabled yet. */
  static rowFor(app: AppCfgsDto, appPlatformTypeId: number): AppCfgPlatformRow | null {
    return (app.platforms ?? []).find((r) => r.appPlatformTypeId === appPlatformTypeId) ?? null;
  }

  /** Enable = an active row with the launcher label. Never a store field: the API assigns the package. */
  static buildCreateReq(appCfgId: number, title: string): AppCfgPlatformCreateReq {
    return { appCfgId, appStateTypeId: APP_STATE_TYPE_ID.Active, title: title.trim() };
  }

  /**
   * The app-name limit a platform's forms enforce: its store's own (Google Play:
   * 30), with a sentence naming the store, else the API's TITLE_MAX.
   */
  static titleLimit(info: AppPlatformInfo): AppPlatformTitleLimit {
    if (info.storeTitleMax == null) return { max: APP_PLATFORM_CONSTRAINTS.TITLE_MAX };
    const store = FACTORY_STORE_INFO[info.store].label;
    return { max: info.storeTitleMax, message: `${store} allows at most ${info.storeTitleMax} characters in an app name` };
  }

  /**
   * The App Name field's description: where the name shows, then — on a store
   * with its own limit — the running count against it ("12/30 characters, the
   * Google Play limit").
   */
  static titleDescription(info: AppPlatformInfo, where: string, value: string): string {
    if (info.storeTitleMax == null) return where;
    const store = FACTORY_STORE_INFO[info.store].label;
    return `${where} ${value.length}/${info.storeTitleMax} characters, the ${store} limit.`;
  }

  /**
   * The app's ACTIVE platforms whose store needs the listing's age rating before
   * the first version is submitted (Google TV); empty = the rating stays optional.
   */
  static platformsRequiringAgeRating(app: AppCfgsDto): AppPlatformInfo[] {
    return Object.values(APP_PLATFORM_INFO).filter(
      (info) =>
        info.listingRequiresAgeRating === true &&
        AppPlatformUtils.rowFor(app, info.typeId)?.appStateTypeId === APP_STATE_TYPE_ID.Active,
    );
  }

  /**
   * The To-submit row for the age rating on a Readiness card that chips these
   * stores: null when none of them needs it; else the chosen rating (green) or
   * Missing (orange), with the button that opens Store Setup's Store Listing.
   */
  static ageRatingReadiness(app: AppCfgsDto, stores: FactoryStoreKey[]): AppAgeRatingReadiness | null {
    const needed = AppPlatformUtils.platformsRequiringAgeRating(app).some((info) => stores.includes(info.store));
    if (!needed) return null;
    const rating = app.listing?.ageRating?.trim() ?? '';
    return rating
      ? { label: rating, color: 'green', action: 'Open' }
      : { label: 'Missing', color: 'orange', action: 'Choose' };
  }

  /**
   * The app's ACTIVE platforms whose store asks whether the app shows ads before
   * the first version is submitted (Fire TV, Google TV); empty = optional.
   */
  static platformsRequiringAdsAnswer(app: AppCfgsDto): AppPlatformInfo[] {
    return Object.values(APP_PLATFORM_INFO).filter(
      (info) =>
        info.listingRequiresAdsAnswer === true &&
        AppPlatformUtils.rowFor(app, info.typeId)?.appStateTypeId === APP_STATE_TYPE_ID.Active,
    );
  }

  /**
   * The To-submit row for the ads answer on a Readiness card that chips these
   * stores: null when none of them asks; else Yes / No (green) or Missing
   * (orange), with the button that opens Store Setup's Store Listing. An absent
   * key is "not answered" (the API drops null fields).
   */
  static adsReadiness(app: AppCfgsDto, stores: FactoryStoreKey[]): AppAdsReadiness | null {
    const needed = AppPlatformUtils.platformsRequiringAdsAnswer(app).some((info) => stores.includes(info.store));
    if (!needed) return null;
    const answer = app.listing?.streamsContainAds;
    if (answer == null) return { label: 'Missing', color: 'orange', action: 'Answer' };
    return { label: answer ? 'Yes' : 'No', color: 'green', action: 'Open' };
  }

  /** Unset store fields arrive as ABSENT keys; the form holds '' for them. */
  static transformRowToFormData(row: AppCfgPlatformRow): AppPlatformFormData {
    return {
      title: row.title ?? '',
      appStateTypeId: row.appStateTypeId,
      storePackageId: row.storePackageId ?? '',
      storeAppId: row.storeAppId ?? '',
    };
  }

  /** The package cannot change while the row carries a store app id (the store knows the app by it). */
  static isPackageLocked(row: AppCfgPlatformRow): boolean {
    return !!row.storeAppId;
  }

  /**
   * An operator may replace (or assign) the package only while no store app id
   * is recorded — the Override, for an app that already exists in a store under
   * another package. Nobody else ever edits it.
   */
  static canOverridePackage(row: AppCfgPlatformRow, isOperator: boolean): boolean {
    return isOperator && !AppPlatformUtils.isPackageLocked(row);
  }

  /**
   * What the person may do with the store app id field:
   * - `readonly`: it is recorded and only an operator changes it;
   * - `needs-package`: nothing to attach it to yet (the API refuses it too);
   * - `editable`: the holder side's one first set, or an operator's edit.
   * `formPackageId` is the form's package value — for everyone but an operator
   * in the Override that is the stored one.
   */
  static storeAppIdState(
    row: AppCfgPlatformRow,
    isOperator: boolean,
    formPackageId: string,
  ): AppPlatformStoreAppIdState {
    if (row.storeAppId && !isOperator) return 'readonly';
    if (formPackageId.trim() === '') return 'needs-package';
    return 'editable';
  }

  /**
   * With an active account that the operator submits from, the operator also
   * creates the app in the store console and records its id — the holder side
   * is told there is nothing to do (the field stays open to them all the same).
   * An operator viewing the tab is never told that: they are the one who does it.
   */
  static operatorRecordsStoreAppId(
    isOperator: boolean,
    account: { state: string; submitter: string } | null | undefined,
  ): boolean {
    return !isOperator && account?.state === 'active' && account.submitter === 'operator';
  }

  /**
   * The update body. A store key travels only when it CHANGED (absent = keep,
   * and the API assigns the package while none is stored). The holder side
   * (everyone but an operator) never sends the package, and sends the store app
   * id only as its first set — a recorded one is not theirs to change (403). An
   * operator sends either key when it changed and never the package while it is
   * locked: a package of '' asks for the ASSIGNED one back (it does not clear),
   * a store app id of '' clears it.
   */
  static transformFormToUpdateReq(
    form: AppPlatformFormData,
    row: AppCfgPlatformRow,
    isOperator: boolean,
  ): AppCfgPlatformUpdateReq {
    const req: AppCfgPlatformUpdateReq = {
      appStateTypeId: form.appStateTypeId,
      title: form.title.trim(),
    };
    const appId = form.storeAppId.trim();
    if (!isOperator) {
      if (!row.storeAppId && appId !== '') req.storeAppId = appId;
      return req;
    }
    const pkg = form.storePackageId.trim();
    if (!AppPlatformUtils.isPackageLocked(row) && pkg !== (row.storePackageId ?? '')) {
      req.storePackageId = pkg;
    }
    if (appId !== (row.storeAppId ?? '')) req.storeAppId = appId;
    return req;
  }

  /**
   * Which form field a refused write belongs to; null = not a store-field
   * refusal, or one on a field this request never carried (the caller shows its
   * ordinary error — the package has no editable field on the holder side).
   * The API sends no error code, so its sentence names the field
   * (AppCfgPlatformRules: "The store package id …" / "The store app id …" /
   * "Store app id is not …" / "Set the store package id before the store app
   * id."); a sentence this does not know falls back to the status: 400 / 422 =
   * a grammar or "package first", 409 = the package is locked or taken. An
   * unknown 403 is about something else (the entitlement).
   */
  static refusalField(
    httpStatusCode: number,
    req: Pick<AppCfgPlatformUpdateReq, 'storePackageId' | 'storeAppId'>,
    apiMessage: string,
  ): AppPlatformStoreField | null {
    if (![400, 403, 409, 422].includes(httpStatusCode)) return null;
    const sent = (field: AppPlatformStoreField) => (req[field] !== undefined ? field : null);
    const sentence = apiMessage.trim();
    if (/^set the store package id before/i.test(sentence)) return sent('storeAppId');
    if (/^(the )?store app id\b/i.test(sentence)) return sent('storeAppId');
    if (/^(the )?store package id\b/i.test(sentence)) return sent('storePackageId');
    switch (httpStatusCode) {
      case 409:
        return sent('storePackageId');
      case 400:
      case 422:
        return sent('storeAppId') ?? sent('storePackageId');
      default:
        return null;
    }
  }

  /**
   * The sentence under the refused field. A 403 on the holder side means the
   * store app id was recorded meanwhile — say who changes it for THIS account
   * (the API can only say "support"); every other refusal carries the API's own
   * sentence.
   */
  static refusalMessage(
    httpStatusCode: number,
    field: AppPlatformStoreField,
    isOperator: boolean,
    apiMessage: string,
    storeAppIdLabel: string,
    supportContact: string,
  ): string {
    if (httpStatusCode === 403 && field === 'storeAppId' && !isOperator) {
      return `This app already has its ${storeAppIdLabel}. Reload the page to see it; contact ${supportContact} to change it.`;
    }
    return apiMessage;
  }

  /** '' is valid (unset); a value follows the platform family's grammar. */
  static isValidStorePackageId(kind: AppPlatformPackageKind, value: string): boolean {
    const v = value.trim();
    if (v === '') return true;
    if (v.length > APP_PLATFORM_CONSTRAINTS.STORE_PACKAGE_ID_MAX) return false;
    return (kind === 'android' ? ANDROID_APPLICATION_ID_PATTERN : APPLE_BUNDLE_ID_PATTERN).test(v);
  }

  /**
   * '' is valid (unset); a value carries no whitespace or control characters and,
   * where the store has its own id grammar (APP_PLATFORM_INFO), follows it.
   */
  static isValidStoreAppId(value: string, pattern?: RegExp): boolean {
    const v = value.trim();
    if (v === '') return true;
    if (v.length > APP_PLATFORM_CONSTRAINTS.STORE_APP_ID_MAX) return false;
    // eslint-disable-next-line no-control-regex
    if (/[\s\u0000-\u001f\u007f]/.test(v)) return false;
    return pattern ? pattern.test(v) : true;
  }

  /** A pasted id loses the whitespace around (and inside) it: no store id carries any. */
  static cleanStoreAppIdInput(value: string): string {
    return value.replace(/\s+/g, '');
  }

  /** The API refuses a store app id on a row without a package. */
  static appIdHasPackage(form: Pick<AppPlatformFormData, 'storePackageId' | 'storeAppId'>): boolean {
    return form.storeAppId.trim() === '' || form.storePackageId.trim() !== '';
  }
}
